import { useState, useEffect } from 'react';
import {
    Box, TextField, Typography, Paper, Chip, Alert,
    InputAdornment, Tooltip, IconButton, Divider,
} from '@mui/material';
import { Calculate, Schedule, Timer, WarningAmber, RestartAlt, LocalPharmacy } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// ─── Types ───────────────────────────────────────────────────────────────────

interface DosingTableEntry {
    weight_min: number;
    weight_max: number | null;
    dose_val: number;
    dose_unit: string;
    label?: string;
}

interface ProductAmountDose {
    dose_val: number | null;
    dose_max: number | null;
    dose_unit: string | null;
    is_per_kg_bodyweight: boolean;
    dosing_table?: DosingTableEntry[];
}

interface ActiveSubstanceDose {
    substance_name: string;
    dose_val: number | null;
    dose_max: number | null;
    dose_unit: string;
    is_per_kg_bodyweight: boolean;
}

interface ScenarioData {
    // New schema
    product_amount_dose?: ProductAmountDose | null;
    active_substance_doses?: ActiveSubstanceDose[];
    // Shared (both schemas)
    frequency?: string | null;
    frequency_days?: number | null;
    doses_per_day?: number | null;
    duration_days?: number | null;
    duration?: string | null;       // legacy string duration
    withdrawal_meat?: string | null;
    withdrawal_milk?: string | null;
    is_single_dose?: boolean | null;
    instructions?: string[] | null;
    label?: string;
    species?: string;
    // Legacy flat fields (backward compat)
    dose_val?: number | null;
    dose_min?: number | null;
    dose_max?: number | null;
    dose_unit?: string;
    is_per_kg_bodyweight?: boolean;
    dose_type?: string;
    dosing_table?: DosingTableEntry[];
}

interface CompositionEntry {
    substance_name?: string;
    name?: string;           // legacy
    quantity: string;
    unit: string;
}

interface DosageCalculatorProps {
    weight?: number | null;
    drugName?: string;
    pharmaceuticalForm?: string;
    scenario?: ScenarioData;
    composition?: CompositionEntry[]; // fallback when active_substance_doses is empty
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const parseQty = (qty: string): number => parseFloat((qty ?? '').replace(',', '.')) || 0;
const compName = (c: CompositionEntry): string => c.substance_name ?? c.name ?? '';

const cleanProductUnit = (unit: string): string =>
    unit.replace(/\/(kg|animal|livre|j|day|jour|semaine|week|mois|month|an|year|poids|bw).*/gi, '').trim();

function inferProductUnit(pharmaceuticalForm: string): string {
    const f = pharmaceuticalForm.toLowerCase();
    if (f.includes('comprim') || f.includes('tablet')) return 'cp';
    if (f.includes('gélule') || f.includes('gelule') || f.includes('capsule')) return 'gélule(s)';
    if (f.includes('solution') || f.includes('injectable') || f.includes('sirop') ||
        f.includes('suspension') || f.includes('buvable') || f.includes('collyre') ||
        f.includes('lyophilisat') || f.includes('liquide')) return 'mL';
    if (f.includes('poudre') || f.includes('prémélange') || f.includes('premelange')) return 'g';
    return '';
}

function fmtDose(val: number): string {
    if (!isFinite(val) || val === 0) return '0';
    if (Math.abs(val) < 0.001) return val.toFixed(4);
    if (Math.abs(val) < 0.1)   return val.toFixed(3);
    if (Math.abs(val) < 10)    return val.toFixed(2);
    return val.toFixed(1);
}

function roundHalf(val: number): number {
    return Math.round(val * 2) / 2;
}

function fmtTabletDose(val: number): string {
    const rounded = roundHalf(val);
    const exact   = fmtDose(val);
    const roundedStr = rounded % 1 === 0 ? String(rounded) : `${rounded}`;
    if (Math.abs(rounded - val) < 0.001) return roundedStr;
    return `${roundedStr} (${exact} exactement)`;
}

function isTabletUnit(unit: string): boolean {
    const u = unit.toLowerCase().trim();
    return u.startsWith('comprim') || u.startsWith('gélule') || u.startsWith('gelule') ||
        u.startsWith('capsule') || u === 'cp';
}

function getTableDose(table: DosingTableEntry[], weight: number): DosingTableEntry | null {
    if (!table.length || weight <= 0) return null;
    return table.find(e => weight >= e.weight_min && (e.weight_max === null || weight < e.weight_max)) ?? null;
}

// Strip /kg, /animal, etc. from unit strings (for substance display)
function substBaseUnit(unit: string): string {
    return unit.replace(/\s*\/\s*(kg|animal|j|day|jour|an|year|semaine|bw).*/gi, '').trim();
}

// ─── DoseInput ── free-typing field that commits on blur/Enter ───────────────

interface DoseInputProps {
    value: number;
    onChange: (raw: string) => void;
    label: string;
    unit: string;
    sx?: object;
}

function DoseInput({ value, onChange, label, unit, sx }: DoseInputProps) {
    const [str, setStr]       = useState(fmtDose(value));
    const [focused, setFocused] = useState(false);

    useEffect(() => {
        if (!focused) setStr(fmtDose(value));
    }, [value, focused]);

    return (
        <TextField
            size="small" type="text"
            label={label}
            value={str}
            onChange={(e) => setStr(e.target.value)}
            onFocus={() => { setFocused(true); setStr(fmtDose(value)); }}
            onBlur={() => { setFocused(false); onChange(str); }}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
            inputProps={{ inputMode: 'decimal' as const }}
            InputProps={{ endAdornment: <InputAdornment position="end"><Typography variant="caption" noWrap>{unit}</Typography></InputAdornment> }}
            sx={sx}
        />
    );
}

// ─── Component ───────────────────────────────────────────────────────────────

export function DosageCalculator({ weight, drugName, pharmaceuticalForm, scenario, composition }: DosageCalculatorProps) {
    const { t } = useTranslation();

    // ── Extract params (new schema with legacy fallback) ──
    const pad       = scenario?.product_amount_dose ?? null;
    const ads       = scenario?.active_substance_doses ?? [];
    const dosesPerDay  = scenario?.doses_per_day ?? 1;
    const isSingleDose = scenario?.is_single_dose ?? false;

    // Product dose source (prefer new schema, fall back to legacy flat fields)
    const padDoseVal  = pad?.dose_val  ?? scenario?.dose_val  ?? scenario?.dose_min ?? null;
    const padDoseMax  = pad?.dose_max  ?? scenario?.dose_max  ?? null;
    const padDoseUnit = pad?.dose_unit ?? scenario?.dose_unit ?? null;
    const padIsPerKg  = pad?.is_per_kg_bodyweight ?? scenario?.is_per_kg_bodyweight ?? false;
    const dosingTable = pad?.dosing_table ?? scenario?.dosing_table ?? null;

    // Per-administration product dose (daily ÷ doses_per_day; table values are already per-admin)
    const padPerAdminVal = padDoseVal !== null
        ? (dosesPerDay > 1 ? padDoseVal / dosesPerDay : padDoseVal)
        : null;
    const padPerAdminMax = padDoseMax !== null
        ? (dosesPerDay > 1 ? padDoseMax / dosesPerDay : padDoseMax)
        : null;

    // Derive product dose from substance mg/kg ÷ composition mg/mL when pad has no dose_val
    // (e.g. SEMINTRA 4 mg/mL: 1 mg/kg ÷ 4 mg/mL = 0.25 mL/kg)
    const derivedProductPerKg = (() => {
        if (padPerAdminVal !== null && padPerAdminVal > 0) return null;
        if (!ads.length || !composition?.length) return null;
        const norm = (s: string) =>
            s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        for (const ad of ads) {
            if (!ad.dose_val || !ad.is_per_kg_bodyweight) continue;
            const adNorm = norm(ad.substance_name);
            const comp = composition.find(c => {
                const cn = norm(compName(c));
                return cn.includes(adNorm) || adNorm.includes(cn);
            });
            if (comp) {
                const conc = parseQty(comp.quantity);
                if (conc > 0) return ad.dose_val / conc;
            }
        }
        return null;
    })();

    // ── State: single scale factor drives all doses proportionally ──
    const [currentWeight, setCurrentWeight] = useState<number>(weight || 0);
    const [scaleFactor, setScaleFactor]     = useState(1.0);
    const [scaleMax, setScaleMax]           = useState(1.0);

    // Sync external weight
    useEffect(() => { if (weight) setCurrentWeight(weight); }, [weight]);

    // Reset scale when scenario changes
    useEffect(() => {
        if (scenario) { setScaleFactor(1.0); setScaleMax(1.0); }
    }, [scenario]);

    // ── Derived ──
    const tableEntry       = dosingTable ? getTableDose(dosingTable, currentWeight) : null;
    const effectiveIsPerKg = (padIsPerKg || derivedProductPerKg !== null) && !tableEntry;

    // RCP "original" product dose (per-admin, tracks table entry automatically with weight)
    const origProductDoseVal = tableEntry ? tableEntry.dose_val
        : derivedProductPerKg !== null ? derivedProductPerKg
        : (padPerAdminVal ?? 0);
    const origProductDoseMax = padPerAdminMax;

    // Current (scaled) product dose
    const productDoseVal = origProductDoseVal * scaleFactor;
    const productDoseMax = origProductDoseMax !== null ? origProductDoseMax * scaleMax : null;

    // Per-admin product total (weight applied for per-kg)
    const productPerAdmin    = effectiveIsPerKg ? productDoseVal * currentWeight : productDoseVal;
    const productMaxPerAdmin = productDoseMax !== null
        ? (effectiveIsPerKg ? productDoseMax * currentWeight : productDoseMax)
        : null;

    // Substance values (scaled from RCP originals)
    const substVal = (i: number) => (ads[i]?.dose_val ?? 0) * scaleFactor;
    const substMax = (i: number) => {
        const m = ads[i]?.dose_max;
        return m !== null && m !== undefined ? m * scaleMax : null;
    };

    // Product unit
    const productUnit = (() => {
        if (tableEntry) return cleanProductUnit(tableEntry.dose_unit);
        if (padDoseUnit) return cleanProductUnit(padDoseUnit);
        if (pharmaceuticalForm) return inferProductUnit(pharmaceuticalForm);
        return '';
    })();

    // Modification
    const EPSILON     = 1e-9;
    const isModified  = Math.abs(scaleFactor - 1) > EPSILON || Math.abs(scaleMax - 1) > EPSILON;

    // Edit handlers: derive new scale from edited value / original
    const handleSubstChange = (i: number, rawVal: string, isMax: boolean) => {
        const n    = parseFloat(rawVal.replace(',', '.'));
        if (!isFinite(n) || n < 0) return;
        const orig = isMax ? (ads[i]?.dose_max ?? 0) : (ads[i]?.dose_val ?? 0);
        if (orig <= 0) return;
        isMax ? setScaleMax(n / orig) : setScaleFactor(n / orig);
    };

    const handleProductChange = (rawVal: string, isMax: boolean) => {
        const n    = parseFloat(rawVal.replace(',', '.'));
        if (!isFinite(n) || n < 0) return;
        const orig = isMax ? origProductDoseMax : origProductDoseVal;
        if (!orig || orig <= 0) return;
        isMax ? setScaleMax(n / orig) : setScaleFactor(n / orig);
    };

    const handleReset = () => { setScaleFactor(1.0); setScaleMax(1.0); };

    // Frequency string
    const freqStr = (): string | null => {
        const fd  = scenario?.frequency_days;
        const dpd = scenario?.doses_per_day;
        if (dpd && dpd > 1) {
            const h = 24 / dpd;
            return `toutes les ${Number.isInteger(h) ? h : h.toFixed(1)}h`;
        }
        if (fd === 1) return '1 fois par jour';
        if (fd !== null && fd !== undefined) {
            if (fd < 1) {
                const h = fd * 24;
                return `toutes les ${Number.isInteger(h) ? h : h.toFixed(1)}h`;
            }
            return `toutes les ${fd % 1 === 0 ? fd : fd.toFixed(1)} jours`;
        }
        return null;
    };

    // Duration string
    const durStr = (): string | null => {
        const raw = scenario?.duration ?? (scenario?.duration_days ? `${scenario.duration_days} jours` : null);
        if (!raw) return null;
        if (/à\s*vie|a\s*vie/i.test(raw)) return 'à vie';
        if (/^continu(el|elle|ment)?$/i.test(raw.trim())) return 'en continu';
        return `pendant ${raw}`;
    };

    if (!scenario) return null;

    const hasWeight      = currentWeight > 0;
    const hasProductMax  = productDoseMax !== null;
    const instructions   = scenario.instructions ?? [];

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>

            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Calculate sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="subtitle2" fontWeight="bold">
                    {t('consultation.dosageCalculator')}
                </Typography>
            </Box>

            {/* Weight */}
            <TextField
                fullWidth size="small"
                label={t('consultation.weightKg')}
                type="number"
                value={currentWeight || ''}
                onChange={(e) => setCurrentWeight(parseFloat(e.target.value.replace(',', '.')) || 0)}
                inputProps={{ min: 0, step: 0.1 }}
                sx={{ mb: 2 }}
            />

            {/* ── Substance dose cards (active_substance_doses) ── */}
            {ads.length > 0 && ads.length <= 6 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {ads.map((ad, i) => {
                        const sv       = substVal(i);
                        const sm       = substMax(i);
                        const hasAdMax = sm !== null;
                        const unit     = substBaseUnit(ad.dose_unit);
                        const total    = ad.is_per_kg_bodyweight && hasWeight ? sv * currentWeight : sv;
                        const maxTotal = hasAdMax ? (ad.is_per_kg_bodyweight && hasWeight ? sm! * currentWeight : sm!) : null;

                        return (
                            <Box key={i} sx={{
                                p: 1.5, borderRadius: 1, border: '1px solid',
                                borderColor: isModified ? 'warning.main' : 'divider',
                                bgcolor: 'background.paper',
                            }}>
                                <Typography variant="caption" color="text.secondary" fontWeight="bold" noWrap
                                    sx={{ display: 'block', mb: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {ad.substance_name}
                                </Typography>

                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <DoseInput
                                        value={sv}
                                        onChange={(raw) => handleSubstChange(i, raw, false)}
                                        label={ad.is_per_kg_bodyweight ? `Dose${hasAdMax ? ' min' : ''}/kg/j` : (hasAdMax ? 'Dose min/j' : 'Dose/j')}
                                        unit={`${unit}${ad.is_per_kg_bodyweight ? '/kg' : ''}`}
                                        sx={{ flex: 1, minWidth: 0 }}
                                    />
                                    {hasAdMax && (
                                        <DoseInput
                                            value={sm!}
                                            onChange={(raw) => handleSubstChange(i, raw, true)}
                                            label={ad.is_per_kg_bodyweight ? 'Dose max/kg/j' : 'Dose max/j'}
                                            unit={`${unit}${ad.is_per_kg_bodyweight ? '/kg' : ''}`}
                                            sx={{ flex: 1, minWidth: 0 }}
                                        />
                                    )}
                                    <Tooltip title="Réinitialiser tout">
                                        <span>
                                            <IconButton size="small" onClick={handleReset}
                                                disabled={!isModified}
                                                color={isModified ? 'warning' : 'default'}
                                                sx={{ flexShrink: 0 }}>
                                                <RestartAlt fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Box>

                                {/* Daily total */}
                                {ad.is_per_kg_bodyweight && hasWeight && (
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                        {maxTotal !== null
                                            ? `Total : ${fmtDose(total)} – ${fmtDose(maxTotal)} ${unit}/j`
                                            : `Total : ${fmtDose(total)} ${unit}/j`}
                                    </Typography>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            )}

            {/* Fallback: direct product dose fields when no substance cards */}
            {ads.length === 0 && (
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    <DoseInput
                        value={productDoseVal}
                        onChange={(raw) => handleProductChange(raw, false)}
                        label={hasProductMax ? 'Dose min' : 'Dose'}
                        unit={productUnit || scenario.dose_unit || ''}
                        sx={{ flex: 1, minWidth: 0, '& .MuiOutlinedInput-root': isModified ? { '& fieldset': { borderColor: 'warning.main' } } : {} }}
                    />
                    {hasProductMax && (
                        <DoseInput
                            value={productDoseMax!}
                            onChange={(raw) => handleProductChange(raw, true)}
                            label="Dose max"
                            unit={productUnit || scenario.dose_unit || ''}
                            sx={{ flex: 1, minWidth: 0, '& .MuiOutlinedInput-root': isModified ? { '& fieldset': { borderColor: 'warning.main' } } : {} }}
                        />
                    )}
                    <Tooltip title="Réinitialiser la posologie RCP">
                        <span>
                            <IconButton size="small" onClick={handleReset}
                                disabled={!isModified} color={isModified ? 'warning' : 'default'}
                                sx={{ flexShrink: 0 }}>
                                <RestartAlt fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            )}

            {/* Derived substance amounts from composition (covers substances missing from active_substance_doses) */}
            {(() => {
                if (!composition?.length || productPerAdmin <= 0) return null;

                // Normalize for accent-insensitive name matching
                const norm = (s: string) =>
                    s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                const coveredNames = ads.map(ad => norm(ad.substance_name));
                const isCovered = (name: string) => {
                    const n = norm(name);
                    return coveredNames.some(c => c.includes(n) || n.includes(c));
                };

                const derived = composition.filter(c => parseQty(c.quantity) > 0 && !isCovered(compName(c)));
                if (!derived.length) return null;

                return (
                    <Box sx={{ mt: 1, p: 1, borderRadius: 1, border: '1px dashed', borderColor: 'divider', bgcolor: 'background.paper' }}>
                        <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block" sx={{ fontSize: '0.72rem', mb: 0.5 }}>
                            Contenu par prise (calculé depuis composition) :
                        </Typography>
                        {derived.map((c, i) => {
                            const amount = productPerAdmin * parseQty(c.quantity);
                            return (
                                <Typography key={i} variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.72rem' }}>
                                    {compName(c)}: <strong>{fmtDose(amount)} {c.unit}</strong>
                                </Typography>
                            );
                        })}
                    </Box>
                );
            })()}

            {/* Modification warning */}
            {isModified && (
                <Alert severity="warning" icon={<WarningAmber fontSize="small" />}
                    sx={{ mt: 1.5, py: 0.5, fontSize: '0.75rem', '& .MuiAlert-message': { py: 0.5 } }}>
                    Posologie modifiée. Se référer aux RCP pour la posologie officielle.
                </Alert>
            )}

            {/* ── Dosage produit (green box) ── */}
            {productUnit && hasWeight && productPerAdmin > 0 && (
                <>
                    <Divider sx={{ my: 1.5 }} />
                    <Box sx={{ p: 1.5, bgcolor: 'success.light', borderRadius: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <LocalPharmacy sx={{ fontSize: '1rem', color: 'success.dark' }} />
                            <Typography variant="caption" color="success.dark" fontWeight="bold">
                                Dosage produit
                            </Typography>
                        </Box>
                        {(() => {
                            const tablet = isTabletUnit(productUnit);
                            const minStr = tablet ? fmtTabletDose(productPerAdmin) : fmtDose(productPerAdmin);
                            const maxStr = productMaxPerAdmin != null
                                ? (tablet ? fmtTabletDose(productMaxPerAdmin) : fmtDose(productMaxPerAdmin))
                                : null;
                            const unitStr = tableEntry?.label
                                ? `${productUnit} (${tableEntry.label})`
                                : productUnit;
                            const amtStr  = maxStr != null
                                ? `${minStr} – ${maxStr} ${unitStr}`
                                : `${minStr} ${unitStr}`;
                            const perTakeStr   = isSingleDose ? 'en dose unique' : 'par prise';
                            const freq         = freqStr();
                            const dur          = durStr();
                            const freqDurLine  = [isSingleDose ? null : freq, dur].filter(Boolean).join(', ');
                            return (
                                <>
                                    <Typography variant="body2" color="success.dark" fontWeight="bold">
                                        {amtStr}{drugName ? ` de ${drugName}` : ''} {perTakeStr}
                                    </Typography>
                                    {freqDurLine && (
                                        <Typography variant="body2" color="success.dark" sx={{ mt: 0.5 }}>
                                            {freqDurLine}
                                        </Typography>
                                    )}
                                </>
                            );
                        })()}
                    </Box>
                </>
            )}

            {/* Frequency / duration chips (fallback when no product summary box) */}
            {!(productUnit && hasWeight && productPerAdmin > 0) &&
                (scenario.frequency || scenario.frequency_days != null || scenario.duration || scenario.duration_days != null) && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                    {(scenario.frequency || scenario.frequency_days != null) && (
                        <Chip icon={<Schedule sx={{ fontSize: '0.875rem !important' }} />}
                            label={`Fréquence : ${scenario.frequency ?? freqStr()}`}
                            size="small" color="info" variant="outlined"
                            sx={{ fontSize: '0.75rem', height: 24 }} />
                    )}
                    {(scenario.duration || scenario.duration_days != null) && (
                        <Chip icon={<Timer sx={{ fontSize: '0.875rem !important' }} />}
                            label={`Durée : ${scenario.duration ?? `${scenario.duration_days} jours`}`}
                            size="small" color="info" variant="outlined"
                            sx={{ fontSize: '0.75rem', height: 24 }} />
                    )}
                </Box>
            )}

            {/* Withdrawal times */}
            {(scenario.withdrawal_meat || scenario.withdrawal_milk) && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                    {scenario.withdrawal_meat &&
                        !['N/A', 'Sans objet', '0 jours', '0', 'null'].includes(scenario.withdrawal_meat) && (
                        <Chip icon={<WarningAmber sx={{ fontSize: '0.875rem !important' }} />}
                            label={`Attente viande : ${scenario.withdrawal_meat}`}
                            size="small" color="warning" variant="outlined"
                            sx={{ fontSize: '0.75rem', height: 24 }} />
                    )}
                    {scenario.withdrawal_milk &&
                        !['N/A', 'Sans objet', '0 jours', '0', 'null'].includes(scenario.withdrawal_milk) && (
                        <Chip icon={<WarningAmber sx={{ fontSize: '0.875rem !important' }} />}
                            label={`Attente lait : ${scenario.withdrawal_milk}`}
                            size="small" color="warning" variant="outlined"
                            sx={{ fontSize: '0.75rem', height: 24 }} />
                    )}
                </Box>
            )}

            {/* Administration instructions */}
            {instructions.length > 0 && (
                <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                    {instructions.map((instr, i) => (
                        <Typography key={i} variant="caption" color="text.secondary" display="block"
                            sx={{ fontSize: '0.72rem', lineHeight: 1.4 }}>
                            {instr}
                        </Typography>
                    ))}
                </Box>
            )}
        </Paper>
    );
}
